import type { TabsProps } from 'antd'
import { Form, Tabs } from 'antd'
import React, { ReactElement, useState } from 'react'
import { CustomModal } from '../../common/CustomModal'
import './index.less'
import { GeneralInfo } from './GeneralInfo'
import { MedispringConnection } from './MedispringConnection'
import { HubsConnection } from './HubsConnection'
import { MonaConnection } from './MonaConnection'
import { useTranslation } from 'react-i18next'
import { useGetPractitionerQuery } from '../../../core/api/practitionerApi'

interface ModalManageAccountProps {
  isVisible: boolean
  onClose: () => void
  healthcarePartyId?: string
  defaultActiveTab?: AccountTabKey
}

export enum AccountTabKey {
  GeneralInfo = 'generalInfo',
  Medispring = 'medispring',
  Hubs = 'hubs',
  Mona = 'mona',
}

export const ModalManageAccount = ({ isVisible, onClose, healthcarePartyId, defaultActiveTab }: ModalManageAccountProps): ReactElement => {
  const { t } = useTranslation()
  const [generalInfoForm] = Form.useForm()
  const [connectMedispringForm] = Form.useForm()
  const [connectHubsForm] = Form.useForm()
  const [connectMonaForm] = Form.useForm()

  const { data: practitionerToBeUpdated, isFetching: isPractitionerFetching } = useGetPractitionerQuery(healthcarePartyId ?? '', { skip: !healthcarePartyId })

  const [activeTabKey, setActiveTabKey] = useState<AccountTabKey>(defaultActiveTab ?? AccountTabKey.GeneralInfo)
  const onTabChange = (key: AccountTabKey) => setActiveTabKey(key)

  const items: TabsProps['items'] = [
    {
      key: AccountTabKey.GeneralInfo,
      label: t('modalManageAccountForm.general.title'),
      children: <GeneralInfo t={t} onClose={onClose} form={generalInfoForm} practitionerId={practitionerToBeUpdated?.id} />,
    },
    {
      key: AccountTabKey.Medispring,
      label: t('modalManageAccountForm.medispring.title'),
      children: <MedispringConnection t={t} practitionerId={practitionerToBeUpdated?.id} form={connectMedispringForm} onClose={onClose} />,
    },
    {
      key: AccountTabKey.Hubs,
      label: t('modalManageAccountForm.hubs.title'),
      children: <HubsConnection t={t} form={connectHubsForm} practitionerId={practitionerToBeUpdated?.id} onClose={onClose} />,
    },
    {
      key: AccountTabKey.Mona,
      label: t('modalManageAccountForm.mona.title'),
      children: <MonaConnection t={t} practitionerId={practitionerToBeUpdated?.id} form={connectMonaForm} onClose={onClose} />,
    },
  ]

  const handleSubmit = () => {
    switch (activeTabKey) {
      case AccountTabKey.GeneralInfo:
        return generalInfoForm.submit()
      case AccountTabKey.Medispring:
        return connectMedispringForm.submit()
      case AccountTabKey.Hubs:
        return connectHubsForm.submit()
      case AccountTabKey.Mona:
        return connectMonaForm.submit()
    }
  }

  return (
    <CustomModal
      isVisible={isVisible}
      handleClose={onClose}
      secondaryBtnTitle={t('manageAccountModal.secondaryBtn')}
      handleClickPrimaryBtn={() => handleSubmit()}
      primaryBtnTitle={t('manageAccountModal.primaryBtn')}
      title={t('manageAccountModal.title')}
    >
      <div className="modalManageAccountForm">
        <Tabs
          centered={true}
          defaultActiveKey={defaultActiveTab ?? AccountTabKey.GeneralInfo}
          items={items}
          onChange={(key) => onTabChange(key as AccountTabKey)}
          style={{ width: '100%' }}
        />
      </div>
    </CustomModal>
  )
}
